/**
 * Memorial archive — name search.
 *
 * Submits the search box to the oitm_memorial_search AJAX endpoint, clears the
 * grid's cards, and injects the returned cards in their place. "Clear search"
 * restores the original server-rendered batch. The intro cell (search box +
 * submit trigger) always stays in place.
 *
 * Class-based to match modal.js; import + instantiate from index.js:
 *
 *   import MemorialSearch from './tools/memorials/assets/memorial-search.js';
 *   const grid = document.querySelector('[data-oitm-memorial-grid]');
 *   if (grid) new MemorialSearch(grid);
 *
 * Reads config from window.oitmMemorialArchive (printed by search.php).
 */
export default class MemorialSearch {
	constructor( grid ) {
		this.grid     = grid;
		this.config   = window.oitmMemorialArchive || {};
		this.intro    = grid.querySelector( '.oitm-memorials__intro' );
		this.form     = grid.querySelector( '[data-oitm-memorial-search]' );
		this.input    = grid.querySelector( '[data-oitm-memorial-search-input]' );
		this.clearBtn = grid.querySelector( '[data-oitm-memorial-search-clear]' );

		// No search UI (e.g. empty archive) — nothing to wire up.
		if ( ! this.form || ! this.input ) {
			return;
		}

		this.originalCards  = null; // Cached server-rendered batch (cloned on first search).
		this.originalOffset = grid.dataset.oitmOffset || '0';
		this.controller     = null; // AbortController for the in-flight request.

		this.bind();
	}

	bind() {
		this.form.addEventListener( 'submit', ( e ) => {
			e.preventDefault();
			this.search( this.input.value.trim() );
		} );

		if ( this.clearBtn ) {
			this.clearBtn.addEventListener( 'click', () => this.clear() );
		}
	}

	// Every grid child except the intro cell is a card.
	cards() {
		return Array.from( this.grid.children ).filter( ( li ) => li !== this.intro );
	}

	cacheOriginal() {
		if ( this.originalCards === null ) {
			this.originalCards = this.cards().map( ( li ) => li.cloneNode( true ) );
		}
	}

	removeCards() {
		this.cards().forEach( ( li ) => li.remove() );
	}

	async search( term ) {
		if ( ! term ) {
			this.clear();
			return;
		}

		this.cacheOriginal();

		// Supersede any request still in flight.
		if ( this.controller ) {
			this.controller.abort();
		}
		this.controller = new AbortController();

		const body = new URLSearchParams( {
			action: this.config.action || 'oitm_memorial_search',
			nonce:  this.config.nonce || '',
			q:      term,
		} );

		this.grid.setAttribute( 'aria-busy', 'true' );

		try {
			const res = await fetch( this.config.ajaxUrl, {
				method:      'POST',
				headers:     { 'Content-Type': 'application/x-www-form-urlencoded' },
				body,
				signal:      this.controller.signal,
				credentials: 'same-origin',
			} );
			const data = await res.json();
			this.render( data );
		} catch ( err ) {
			if ( err.name !== 'AbortError' ) {
				this.renderMessage( 'Search is unavailable right now. Please try again.', 'data-oitm-search-error' );
			}
		} finally {
			this.grid.removeAttribute( 'aria-busy' );
		}
	}

	render( data ) {
		const ok    = data && data.success && data.data;
		const html  = ok ? data.data.html : '';
		const count = ok ? data.data.count : 0;

		this.removeCards();

		if ( count > 0 && html ) {
			this.intro.insertAdjacentHTML( 'afterend', html );
		} else {
			this.renderMessage( 'No results found.', 'data-oitm-search-empty', false );
		}

		if ( this.clearBtn ) {
			this.clearBtn.hidden = false;
		}
		this.grid.dataset.oitmSearching = 'true';
	}

	// Replaces the cards with a single message cell. `wipe` controls whether
	// to clear first (render() already wiped before calling).
	renderMessage( text, attr, wipe = true ) {
		if ( wipe ) {
			this.removeCards();
		}
		const li = `<li class="oitm-memorials__empty" ${ attr }>${ text }</li>`;
		this.intro.insertAdjacentHTML( 'afterend', li );
		if ( this.clearBtn ) {
			this.clearBtn.hidden = false;
		}
	}

	clear() {
		// No active search — nothing to restore. Tidy the input and button and
		// leave the grid exactly as it is.
		if ( ! this.originalCards ) {
			this.input.value = '';
			if ( this.clearBtn ) {
				this.clearBtn.hidden = true;
			}
			return;
		}

		this.removeCards();

		const frag     = document.createDocumentFragment();
		const restored = this.originalCards.length;
		this.originalCards.forEach( ( li ) => frag.appendChild( li.cloneNode( true ) ) );
		this.intro.after( frag );

		// Reset pagination offset to the restored count so scroll loading
		// (next phase) resumes from the right place.
		this.grid.dataset.oitmOffset = String( restored || this.originalOffset );
		delete this.grid.dataset.oitmSearching;

		this.originalCards = null; // Back to "no active search"; next search re-snapshots.
		this.input.value   = '';
		if ( this.clearBtn ) {
			this.clearBtn.hidden = true;
		}
		this.input.focus();
	}
}
